import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, initialUserDetails, userDetailsModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import {removeUserFromLocalStorage} from '../../../Utils/constants'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: string | undefined
  userDetails: userDetailsModel
  setCurrentUser: Dispatch<SetStateAction<string | undefined>>
  changeUserDetails: (details: Partial<userDetailsModel>) => void
  removeUserDetails: () => void
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  userDetails: initialUserDetails,
  currentUser: undefined,
  changeUserDetails: (details: Partial<userDetailsModel>) => {},
  removeUserDetails: () => {},
  setCurrentUser: () => {},
  saveAuth: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<string | undefined>()
  const [userDetails, setUserDetails] = useState<userDetailsModel>(initialUserDetails)
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const changeUserDetails = (details: Partial<userDetailsModel>) => {
    setUserDetails((prev) => ({...prev, ...details}))
  }

  const removeUserDetails = () => {
    setUserDetails(initialUserDetails)
    removeUserFromLocalStorage()
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    removeUserDetails()
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        userDetails,
        changeUserDetails,
        removeUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  const removeUser = () => {
    setCurrentUser(undefined)
  }
  useEffect(() => {
    setTimeout(() => {
      const tokenData = authHelper.getAuth()
      const token = tokenData?.apiToken
      if (token) {
        setCurrentUser(token as unknown as string)
      } else {
        removeUser()
      }
      setShowSplashScreen(false)
    }, 3500)
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
